import { isEqual, flowRight, isString, noop, uniq, debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import URI from 'urijs';
import { connect } from '../../../common/components/runtime-context';
import { WixRicosEditor } from '@wix/ricos-editor';
import { Gateway } from 'react-gateway';
import { RichContentEditor as Editor } from 'wix-rich-content-editor';
import { TOOLBARS } from 'wix-rich-content-editor-common';
import {
  FILE_UPLOAD_SIZE_LIMIT_MB,
  VIDEO_UPLOAD_SIZE_LIMIT_MB,
} from '@wix/communities-forum-client-commons/dist/src/constants/media-limits';
import withTranslate from '@wix/communities-forum-client-commons/dist/src/hoc/with-translate';
import withAuth from '../../hoc/with-auth';
import withFontClassName from '../../hoc/with-font-class-name';
import withDeviceType from '../../hoc/with-device-type';
import withPermissions from '../../hoc/with-permissions';
import {
  getEnabledRcePlugins,
  getIsSocialSharingLinksEnabled,
  getIsUploadLimiterEnabled,
  getUploadSizeLimit,
} from '../../selectors/app-settings-selectors';
import { isExperimentEnabled } from '../../selectors/experiments-selectors';
import { getIsStorageLimitReached } from '../../selectors/forum-data-selectors';
import { getUploadedRegistry } from '../../selectors/uploaded-registry-selectors';
import {
  isDashboard,
  isEditor,
  getIsMobile,
  getInstance,
  getLanguage,
  getApiBaseUrl,
} from '../../../common/store/basic-params/basic-params-selectors';
import getImageUrl from '@wix/communities-forum-client-commons/dist/src/services/get-image-url';
import { PLUGINS } from '@wix/communities-forum-client-commons/dist/src/constants/plugins-constants';
import { createSiteMembersFetchService } from '@wix/communities-forum-client-commons/dist/src/services/mentions-services';
import { HTML_IFRAME_SRC, MAX_PINTEREST_IMAGE_SIZE } from './utils';
import { SIDE_TOOLBAR_OFFSET, CONTENT_COMMIT_DELAY } from './constants';
import { getLocation, getSectionUrl } from '../../../common/store/location/location-selectors';
import { supportedMimeTypes } from '../../../common/services/wix-media/supported-mime-types';
import { uploadFileToWixMedia } from '../../../common/services/wix-media/wix-media-file-upload-service';
import { wrapUploadImageAction } from '../../actions/image-upload/upload-wrapper';
import {
  getTextColorSchema,
  styleSelectionPredicate,
  viewerCustomStyleFn,
  customStyleFn,
} from './text-color-utils';
import {
  ALL_PLUGINS,
  DEFAULT_PLUGINS,
  PLUGINS_WITH_BOTTOM_TOOLBAR,
  DISABLED_PLUGINS_ON_MOBILE,
  DISABLED_PLUGINS_ON_MOBILE_POST_PAGE_REVAMP,
  mapConfigToPlugins,
} from './plugins';
import { VISITOR_UPLOADS_FOLDER_ID, RCE_FAILED_UPLOAD_ID } from '../../constants/upload';
import {
  RCE_FILE_TOO_BIG,
  RCE_FILE_UPLOAD_FAILURE,
  RCE_FILE_UPLOAD_LIMITED,
  RCE_FILE_SIZE_LIMIT_REACHED,
  RCE_FILE_STORAGE_QUOTA_REACHED,
} from '../messages/message-types';
import { isWithinSizeLimits } from '@wix/communities-forum-client-commons/dist/src/services/wix-media/is-within-size-limits';
import { uploadVideoToWixMedia } from '../../../common/services/wix-media/wix-media-video-upload-service';
import { EXPERIMENT_POSTPAGE_REVAMP } from '@wix/communities-forum-client-commons/dist/src/constants/experiments';
import {
  ExternalPluginToolbar,
  ExternalFormattingToolbar,
  PLUGIN_BUTTONS,
  FORMATTING_BUTTONS,
} from './external-toolbars';
import theme from './theme';
import styles from './rich-content-editor.scss';
import { EditorState, convertFromRaw, SelectionState } from '@wix/draft-js';
import { createInitialDraftJsContentWithText } from '../../services/create-initial-draft-content-with-text';

// todo - remove upload mocking after entity error flag is implemented in rce
const MOCK_FAILED_IMAGE_UPLOAD_DATA = { data: { id: RCE_FAILED_UPLOAD_ID } };
const MOCK_FAILED_VIDEO_UPLOAD_DATA = { data: { pathname: RCE_FAILED_UPLOAD_ID } };
const MOCK_FAILED_FILE_UPLOAD_DATA = { data: { id: RCE_FAILED_UPLOAD_ID } };
//
class RichContentEditor extends Component {
  state = {
    userColors: [],
    editorKey: false, // force re-render after updating plugins in editor/ADI
  };

  constructor(props) {
    super(props);
    this.initEditorProps();
  }

  componentDidCatch(e) {
    console.error(e);
  }

  componentDidMount() {
    if (this.props.focus) {
      setTimeout(this.editor.focus, 50);
    }
    this.props.onMount && this.props.onMount(this.updateEditorState);
  }

  componentDidUpdate(prevProps) {
    this.updateUploadedEntityCallbacks();
    if (!isEqual(this.props.enabledPlugins, prevProps.enabledPlugins)) {
      this.initEditorProps();
      this.setState({ editorKey: !this.state.editorKey });
    }
  }

  updateEditorState = getText => {
    return new Promise(res =>
      setTimeout(() => {
        const text = getText();
        if (text) {
          const rawContent = createInitialDraftJsContentWithText(text);
          const contentState = convertFromRaw(rawContent);

          const blockMap = contentState.getBlockMap();
          const key = blockMap.last().getKey();
          const length = blockMap.last().getLength();

          const selection = new SelectionState({
            anchorKey: key,
            anchorOffset: length,
            focusKey: key,
            focusOffset: length,
          });

          const newEditorState = EditorState.createWithContent(contentState);
          this.editor.editor.editor.updateEditorState(
            EditorState.forceSelection(newEditorState, selection),
          );
          res();
        }
      }),
    );
  };

  getImagePluginConfig() {
    const { isSocialSharingLinksEnabled: showPin } = this.props;
    const config = {
      imageProps: showPin
        ? src => ({
            'data-pin-url': this.props.pageUrl,
            'data-pin-media': isString(src)
              ? src
              : getImageUrl(src, MAX_PINTEREST_IMAGE_SIZE, MAX_PINTEREST_IMAGE_SIZE),
          })
        : { 'data-pin-nopin': true },
      toolbar: {
        hidden: ['sizeFullWidth'],
      },
    };

    if (!this.props.showAdvancedImageSettings) {
      config.toolbar.hidden.push('link', 'settings');
    }

    return config;
  }

  getVideoPluginConfig() {
    const {
      uploadVideoToWixMedia,
      isOwner,
      waitForSuccessfulResponsePromisified,
      showMessage,
      isUploadLimiterEnabled,
      uploadSizeLimit,
      userEventsHitUploadSizeLimit,
      origin,
      t,
    } = this.props;
    const config = {
      toolbar: {
        hidden: [],
      },
      getVideoUrl: src => `https://video.wixstatic.com/${src.pathname}`,
      enableCustomUploadOnMobile: true,
    };
    config.handleFileUpload = (file, updateEntity) => {
      const uploadLimit = isUploadLimiterEnabled ? uploadSizeLimit : VIDEO_UPLOAD_SIZE_LIMIT_MB;
      if (!isWithinSizeLimits(file, uploadLimit) && !isOwner) {
        if (isUploadLimiterEnabled) {
          // rce bug, updateEntity doesn't work here without setTimeout
          return setTimeout(() => {
            updateEntity(MOCK_FAILED_VIDEO_UPLOAD_DATA);

            updateEntity({
              error: {
                msg: t('rich-content-editor.upload-size-limit', { amount: uploadSizeLimit }),
              },
            });
            userEventsHitUploadSizeLimit({
              limit: uploadSizeLimit,
              fileSize: file.size,
              fileType: 'video',
              type: origin,
            });
          }, 0);
        }
        return showMessage(RCE_FILE_TOO_BIG);
      }

      const folderId = isOwner ? null : VISITOR_UPLOADS_FOLDER_ID;
      uploadVideoToWixMedia(file, folderId)
        .then(({ promise }) => promise)
        .then(uploadData => {
          waitForSuccessfulResponsePromisified(
            `https://video.wixstatic.com/${uploadData.file_output.video[0].url}`,
          ).then(() => {
            const data = {
              pathname: uploadData.file_output.video[0].url,
              thumbnail: {
                pathname: uploadData.file_output.image[0].url,
                width: uploadData.file_output.image[0].width,
                height: uploadData.file_output.image[0].height,
              },
            };

            updateEntity({ data });
          });
        })
        .catch(e => {
          const getErrorMessage = e => {
            switch (e) {
              case 406:
                return 'rich-content-editor.site-storage-limited';
              case 429:
                return 'rich-content-editor.video-upload-limited';
              default:
                return 'rich-content-editor.video-upload-failure';
            }
          };
          const message = getErrorMessage(e);

          if (e === 406) {
            updateEntity(MOCK_FAILED_VIDEO_UPLOAD_DATA);
          }

          updateEntity({
            error: { msg: t(message) },
          });
        });
    };
    return config;
  }

  initEditorProps() {
    const {
      style,
      titleFontClassName,
      contentFontClassName,
      plugins,
      instance,
      themeGetter,
      enabledPlugins,
      compact,
      sideToolbarOffset,
      uploadFileToWixMedia,
      showMessage,
      apiBaseUrl,
      fileUploaded,
      origin,
      isMobile,
      isInline,
      isAuthenticated,
      isUploadLimiterEnabled,
      uploadSizeLimit,
      isStorageLimitReached,
      userEventsHitUploadSizeLimit,
      isOwner,
      isPostPageRevampEnabled,
    } = this.props;

    const colorScheme = getTextColorSchema(style);

    this.config = {
      [PLUGINS.FILE_UPLOAD]: {
        accept: supportedMimeTypes.join(','),
        onFileSelected: (file, updateEntity) => {
          const uploadLimit = isUploadLimiterEnabled ? uploadSizeLimit : FILE_UPLOAD_SIZE_LIMIT_MB;

          if (isStorageLimitReached) {
            updateEntity(MOCK_FAILED_FILE_UPLOAD_DATA);
            return showMessage(RCE_FILE_STORAGE_QUOTA_REACHED);
          }

          if (!isWithinSizeLimits(file, uploadLimit) && !isOwner) {
            if (isUploadLimiterEnabled) {
              userEventsHitUploadSizeLimit({
                limit: uploadSizeLimit,
                fileSize: file.size,
                fileType: 'file',
                type: origin,
              });
              updateEntity(MOCK_FAILED_FILE_UPLOAD_DATA);
              return showMessage(RCE_FILE_SIZE_LIMIT_REACHED, { uploadSizeLimit });
            }
            return showMessage(RCE_FILE_TOO_BIG);
          }
          const name = file.name;
          const filenameParts = name.split('.');
          const type = name.includes('.') ? filenameParts[filenameParts.length - 1] : '';

          uploadFileToWixMedia(file)
            .then(uploadData => {
              const data = {
                name,
                type,
                mimeType: uploadData.mimeType,
                path: uploadData.path,
                size: uploadData.size,
                id: uploadData.id,
              };

              updateEntity({ data });
              fileUploaded({
                fileId: uploadData.id,
                fileExtension: type,
                isSuccessful: true,
                mimeType: uploadData.mimeType,
                size: uploadData.size,
                origin,
              });
            })
            .catch(e => {
              e === 429 && showMessage(RCE_FILE_UPLOAD_LIMITED);
              e === 400 && showMessage(RCE_FILE_UPLOAD_FAILURE);
              fileUploaded({
                fileId: null,
                fileExtension: type,
                isSuccessful: false,
                mimeType: file.type,
                size: file.size,
                origin,
              });
            });
        },
      },
      [PLUGINS.LINK_PREVIEW]: {
        enableEmbed: true,
      },
      [PLUGINS.GIPHY]: {
        giphySdkApiKey: '8LVphUXpyDK77BY7BhYMXgFU6TwhSKkA',
        componentDataDefaults: { config: { size: 'small', alignment: 'center' } },
        insertToolbars: [TOOLBARS.SIDE, TOOLBARS.FOOTER],
      },
      [PLUGINS.TEXT_COLOR]: {
        colorScheme,
        styleSelectionPredicate: styleSelectionPredicate(colorScheme),
        customStyleFn: customStyleFn(viewerCustomStyleFn(colorScheme)),
        getUserColors: () => this.state.userColors,
        onColorAdded: c => this.setState({ userColors: this.state.userColors.concat(c) }),
      },
      [PLUGINS.HASHTAG]: {
        createHref: () => '',
        onClick: noop,
      },
      [PLUGINS.IMAGE]: this.getImagePluginConfig(),
      [PLUGINS.VIDEO]: this.getVideoPluginConfig(),
      [PLUGINS.HTML]: {
        htmlIframeSrc: HTML_IFRAME_SRC,
      },
      [PLUGINS.MENTIONS]: {
        getMentions: createSiteMembersFetchService({ instance, apiBaseUrl }),
        entryHeight: 30,
        visibleItemsBeforeOverflow: 5,
      },
    };

    let pluginsToGet = uniq(
      (plugins || ALL_PLUGINS).filter(p => enabledPlugins.includes(p)).concat(DEFAULT_PLUGINS),
    );

    if (!isAuthenticated) {
      pluginsToGet = pluginsToGet.filter(p => p !== PLUGINS.FILE_UPLOAD);
    }

    const PluginsToDisable = isPostPageRevampEnabled
      ? DISABLED_PLUGINS_ON_MOBILE_POST_PAGE_REVAMP
      : DISABLED_PLUGINS_ON_MOBILE;

    const mobilePluginsToGet = pluginsToGet.filter(p => !PluginsToDisable.includes(p));

    this.plugins = isMobile
      ? mapConfigToPlugins(this.config, mobilePluginsToGet)
      : mapConfigToPlugins(this.config, pluginsToGet);

    const hasBottomToolbarPlugins = pluginsToGet.some(p => PLUGINS_WITH_BOTTOM_TOOLBAR.includes(p));

    this.helpers = {
      onVideoSelected: this.handleVideoSelect,
      onFilesChange: this.handleFileChange,
    };

    this.theme = themeGetter({
      style,
      titleFontClassName,
      contentFontClassName,
      compact,
      hasBottomToolbarPlugins,
      isMobile,
    });

    this.toolbarSettings = {
      getToolbarSettings: ({ textButtons }) => {
        const getTextButtons = () => {
          const buttonsToExclude = ['AddPlugin', 'TextColor_1', 'Link_0'];
          const mobileButtons = textButtons.mobile.filter(
            button => !buttonsToExclude.includes(button),
          );
          return { mobile: { ios: mobileButtons, android: mobileButtons } };
        };

        const toolbarSettings = [
          {
            name: TOOLBARS.SIDE,
            getPositionOffset: () => sideToolbarOffset,
            shouldCreate: () => ({
              desktop: true,
              mobile: {
                ios: !isPostPageRevampEnabled,
                android: !isPostPageRevampEnabled,
              },
            }),
          },
          {
            name: TOOLBARS.FOOTER,
            shouldCreate: () => ({
              desktop: !compact,
              mobile: { ios: false, android: false },
            }),
          },
          {
            name: TOOLBARS.MOBILE,
            shouldCreate: () => ({
              desktop: false,
              mobile: {
                ios: isMobile && !isInline && !isPostPageRevampEnabled,
                android: isMobile && !isInline && !isPostPageRevampEnabled,
              },
            }),
          },
          {
            name: TOOLBARS.INSERT_PLUGIN,
            shouldCreate: () => ({
              desktop: false,
              mobile: {
                ios: isPostPageRevampEnabled && isMobile,
                android: isPostPageRevampEnabled && isMobile,
              },
            }),
            getButtons: () => ({ mobile: { ios: PLUGIN_BUTTONS, android: PLUGIN_BUTTONS } }),
          },
          {
            name: TOOLBARS.FORMATTING,
            shouldCreate: () => ({
              desktop: false,
              mobile: {
                ios: isPostPageRevampEnabled && isMobile,
                android: isPostPageRevampEnabled && isMobile,
              },
            }),
            getButtons: () => ({
              mobile: { ios: FORMATTING_BUTTONS, android: FORMATTING_BUTTONS },
            }),
          },
        ];

        if (isPostPageRevampEnabled) {
          toolbarSettings.push({
            name: TOOLBARS.MOBILE,
            getButtons: getTextButtons,
          });
        }
        return toolbarSettings;
      },
    };
  }

  registerUploadedCallback = (identifier, cb) => {
    this.imgUpdateEntityCallbacks = this.imgUpdateEntityCallbacks || {};
    this.imgUpdateEntityCallbacks[identifier] = this.imgUpdateEntityCallbacks[identifier] || [];
    this.imgUpdateEntityCallbacks[identifier].push(cb);
  };

  updateUploadedEntityCallbacks = () => {
    if (!this.imgUpdateEntityCallbacks) {
      return;
    }

    Object.entries(this.props.uploadedRegistry).forEach(([uploadId, file]) => {
      const callbacks = this.imgUpdateEntityCallbacks[uploadId];
      if (callbacks) {
        callbacks.forEach(cb => cb(file));

        delete this.imgUpdateEntityCallbacks[uploadId];
        this.props.removeItemFromUploadedRegistry(uploadId);
      }
    });
  };

  handleFileChange = (imgFile, updateEntity) => {
    const uploadId = Date.now();
    const {
      isUploadLimiterEnabled,
      uploadSizeLimit,
      userEventsHitUploadSizeLimit,
      origin,
      isOwner,
      t,
    } = this.props;
    if (isUploadLimiterEnabled && !isWithinSizeLimits(imgFile, uploadSizeLimit) && !isOwner) {
      userEventsHitUploadSizeLimit({
        limit: uploadSizeLimit,
        fileSize: imgFile.size,
        fileType: 'image',
        type: origin,
      });

      updateEntity(MOCK_FAILED_IMAGE_UPLOAD_DATA);

      return updateEntity({
        error: { msg: t('rich-content-editor.upload-size-limit', { amount: uploadSizeLimit }) },
      });
    }

    this.registerUploadedCallback(uploadId, imageData => {
      updateEntity({
        data: {
          id: imageData.file_name,
          ...imageData,
        },
      });
    });

    this.props.uploadImage(imgFile, uploadId).then(({ promise }) => {
      promise.catch(e => {
        if (e === 406) {
          updateEntity(MOCK_FAILED_IMAGE_UPLOAD_DATA);

          return updateEntity({ error: { msg: t('rich-content-editor.site-storage-limited') } });
        }
      });
    });
  };

  handleVideoSelect = (url, updateEntity) => {
    if (typeof url === 'object') {
      updateEntity({
        oembed: {
          author_url: '',
          thumbnail_url: `https://static.wixstatic.com/${url.thumbnail.pathname}`,
          width: url.thumbnail.width,
          height: url.thumbnail.height,
          author_name: '',
          thumbnail_height: url.thumbnail.height,
          version: '1.0',
          provider_url: '',
          thumbnail_width: url.thumbnail.width,
          type: 'video',
          provider_name: '',
          html: '',
          video_url: `https://video.wixstatic.com/${url.pathname}`,
        },
      });
      return;
    }

    this.registerUploadedCallback(url, ({ isSuccessful, _original_url, ...oembed }) => {
      updateEntity({ oembed: isSuccessful ? oembed : null });
    });
    this.props.fetchOembed(url);
  };

  onChange = debounce(async content => this.props.onChange(await content), CONTENT_COMMIT_DELAY, {
    leading: true,
    maxWait: 2000,
  });

  flushOnBlur = () => {
    if (this.props.onChange) {
      this.onChange(this.editor.getContent());
      this.onChange.flush();
    }
  };

  handleSetRef = ref => {
    this.editor = ref;
    this.props.setRef && this.props.setRef(ref);
  };

  render() {
    const {
      initialState,
      onChange,
      locale,
      isMobile,
      instance,
      isInline,
      placeholder,
      isPostPageRevampEnabled,
    } = this.props;

    const renderExternalToolbar = isMobile && isPostPageRevampEnabled;
    const mobileHeaderContainer =
      typeof document !== 'undefined' && document.getElementById('mobile-header');

    return (
      <div className={styles.container}>
        <WixRicosEditor
          biSettings={{ consumer: 'communities-forum-client' }}
          ref={this.handleSetRef}
          content={initialState}
          locale={locale}
          placeholder={placeholder}
          instance={instance}
          plugins={this.plugins}
          cssOverride={this.theme}
          toolbarSettings={{
            ...this.toolbarSettings,
            textToolbarContainer:
              isMobile && !isInline && !isPostPageRevampEnabled ? mobileHeaderContainer : '',
          }}
          linkSettings={{ anchorTarget: '_blank', relValue: 'nofollow ugc' }}
          linkPanelSettings={{
            blankTargetToggleVisibilityFn: () => false,
            nofollowRelToggleVisibilityFn: () => false,
          }}
          isMobile={isMobile}
          onChange={onChange && this.onChange}
        >
          <Editor helpers={this.helpers} onBlur={this.flushOnBlur} />
        </WixRicosEditor>
        {renderExternalToolbar && this.editor && (
          <Gateway into="external-plugin-toolbar">
            <ExternalPluginToolbar
              key="ExternalPluginToolbar"
              {...this.editor.getToolbarProps(TOOLBARS.INSERT_PLUGIN)}
            />
          </Gateway>
        )}
        {renderExternalToolbar && this.editor && (
          <Gateway into="external-formatting-toolbar">
            <ExternalFormattingToolbar
              key="ExternalFormattingToolbar"
              {...this.editor.getToolbarProps(TOOLBARS.FORMATTING)}
            />
          </Gateway>
        )}
      </div>
    );
  }
}

RichContentEditor.propTypes = {
  initialState: PropTypes.object,
  onChange: PropTypes.func,
  uploadImage: PropTypes.func,
  fetchOembed: PropTypes.func,
  style: PropTypes.object,
  titleFontClassName: PropTypes.string,
  contentFontClassName: PropTypes.string,
  plugins: PropTypes.arrayOf(PropTypes.oneOf(ALL_PLUGINS)),
  isEditor: PropTypes.bool,
  isDashboard: PropTypes.bool,
  showAdvancedImageSettings: PropTypes.bool,
  locale: PropTypes.string,
  sectionUrl: PropTypes.string,
  compact: PropTypes.bool,
  pageUrl: PropTypes.string,
  isMobile: PropTypes.bool,
  instance: PropTypes.string,
  themeGetter: PropTypes.func,
  enabledPlugins: PropTypes.array,
  sideToolbarOffset: PropTypes.object,
  isSocialSharingLinksEnabled: PropTypes.bool,
  focus: PropTypes.bool,
  onFocus: PropTypes.func,
  isInline: PropTypes.bool,
  uploadedRegistry: PropTypes.array,
  removeItemFromUploadedRegistry: PropTypes.func,
  uploadFileToWixMedia: PropTypes.func,
  uploadVideoToWixMedia: PropTypes.func,
  showMessage: PropTypes.func,
  fileUploaded: PropTypes.func,
  waitForSuccessfulResponsePromisified: PropTypes.func,
  apiBaseUrl: PropTypes.string,
  origin: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  t: PropTypes.func,
  isOwner: PropTypes.bool,
  setRef: PropTypes.func,
};

RichContentEditor.defaultProps = {
  showAdvancedImageSettings: true,
  compact: false,
  isInline: false,
  themeGetter: theme,
  sideToolbarOffset: SIDE_TOOLBAR_OFFSET,
};

const mapRuntimeToProps = (state, ownProps, actions, host) => {
  return {
    style: host.style,
    isEditor: isEditor(state),
    isDashboard: isDashboard(state),
    locale: getLanguage(state),
    pageUrl: new URI(getLocation(state).url).search('').toString(),
    instance: getInstance(state),
    sectionUrl: getSectionUrl(state),
    enabledPlugins: getEnabledRcePlugins(state, host.style),
    isSocialSharingLinksEnabled: getIsSocialSharingLinksEnabled(state, host.style),
    uploadedRegistry: getUploadedRegistry(state),
    removeItemFromUploadedRegistry: actions.removeItemFromUploadedRegistry,
    uploadImage: wrapUploadImageAction(actions.uploadImageRCEPromisified, getIsMobile(state)),
    fetchOembed: actions.fetchOembed,
    fileUploaded: actions.fileUploaded,
    showMessage: actions.showMessage,
    waitForSuccessfulResponsePromisified: actions.waitForSuccessfulResponsePromisified,
    uploadFileToWixMedia: uploadFileToWixMedia(actions.requestCredentialsV2Promisified),
    uploadVideoToWixMedia: uploadVideoToWixMedia(actions.requestCredentialsPromisified),
    apiBaseUrl: getApiBaseUrl(state),
    isPostPageRevampEnabled: isExperimentEnabled(state, EXPERIMENT_POSTPAGE_REVAMP),
    isUploadLimiterEnabled: getIsUploadLimiterEnabled(state, host.style),
    uploadSizeLimit: getUploadSizeLimit(state, host.style),
    isStorageLimitReached: getIsStorageLimitReached(state),
    userEventsHitUploadSizeLimit: actions.userEventsHitUploadSizeLimit,
  };
};

export default flowRight(
  withAuth,
  withTranslate,
  connect(mapRuntimeToProps),
  withDeviceType,
  withFontClassName,
  withPermissions,
)(RichContentEditor);
